import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import {
  Button,
  Spin,
  Modal,
  Table
} from 'antd';
import Chart from 'chart.js/auto';
import { Bar } from 'react-chartjs-2';
import {
  notificationsAtom,
  graphsDateRangeAtom
} from '../../../../../atoms/Atoms';
import {
  randomColor,
  thousand,
  toCurrency,
} from '../../../../../functions';
import api from '../../../../../api/api';
import Card from '../../../../shared-components/container/Card';
import envConfig from '../../../../../envConfig';

function TopClosersGraph({ filterType, orgReferralCode }) {
  const orgCode = orgReferralCode;  
  const filter = filterType;  
  const { t } = useTranslation();
  const chartRef = useRef();
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [graphsDateRange, setGraphsDateRange] = useRecoilState(graphsDateRangeAtom);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [userSummary, setUserSummary] = useState(null);
  const [showTable, setShowTable] = useState(false);

  function getData() {
    setLoading(true);
    const startDate = dayjs(graphsDateRange.start);
    const endDate = dayjs(graphsDateRange.end);
    const startString = startDate.format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    api.post(`reports/top/topclosers?top=5${orgCode ? `&orgReferralCode=${orgCode}` : ''}`, {
      StartTime: startString,
      EndTime: endString,
      Filter: filter
    })
    .then((response) => {
      setLoading(false);
      if (response.data.success) {
        setReportData(response.data.data);
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          },
        ]);
      }
    })
    .catch((error) => {
      setLoading(false);
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: t('error_getting_data'),
          error: true,
        },
      ]);
    });
  }

  function getSummary() {
    if (selectedUser) {
      setLoadingUserData(true);
      api.get(`reports/summary/closer?userId=${selectedUser.userId}`)
      .then((response) => {
        setLoadingUserData(false);
        if (response.data.success) {
          setUserSummary(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoadingUserData(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: t('error_getting_data'),
            error: true,
          },
        ]);
      });
    }
  }

  useEffect(() => {
    if (graphsDateRange.reload) {
      getData();
      setGraphsDateRange({
        ...graphsDateRange,
        reload: false
      });
    }
  }, [graphsDateRange.reload]);

  useEffect(() => {
      setUserSummary(null);
      if (selectedUser) {
        getSummary();
      }
    }, [selectedUser]);

  return (
    <div className="border-2 shadow-lg pb-20 pt-4">
      <Spin spinning={loading}>
        <div className="text-center" style={{ height: 400 }}>
          <p className="font-bold">{filter === 0 ? `${t('top_closers')} (${t('agreements')})` : (filter === 1 ? `${t('top_closers')} (${t('installs')})` : `${t('top_closers')} (${t('signed')})`)}</p>
          <Bar
            ref={chartRef}
            data={{
              labels: _.map(_.orderBy(reportData, (d) => d.count, 'desc'), (s) => s.label),
              datasets: [
                {
                  label: t('count'),
                  backgroundColor: envConfig.GraphColor,
                  borderWidth: 1,
                  data: _.map(_.orderBy(reportData, (d) => d.count, 'desc'), (x) => x.count)
                }
              ]
            }}
            options={{
              maintainAspectRatio: false,
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                  display: false
                },
                title: {
                  display: false,
                },
                datalabels: {
                  color: 'black',
                  formatter: (value, context) => thousand(value),
                  labels: {
                    title: {
                      font: {
                        weight: 'regular'
                      }
                    }
                  }
                }
              },
              onClick: (event, elements, chart) => {
                setSelectedUser(reportData[elements[0].index]);
              }
            }}
          />
          <div className="text-center flex, space-x-2">
            <Button
              type="primary"
              size="small"
              onClick={() => getData()}
              className="mt-4"
            >
              {t('refresh')}
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => setShowTable(true)}
              className="mt-4"
            >
              {t('view_table')}
            </Button>
          </div>
        </div>
      </Spin>
      <Modal
        title={`${t('summary')} - ${selectedUser?.label}`}
        open={selectedUser}
        onCancel={() => {
          setSelectedUser(null);
        }}
        footer={[]}
      >
        <div className="grid gap-4 mt-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          <Spin spinning={loadingUserData}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('no_appointment')}
              description={thousand(userSummary?.totalLeadsNoAppointment)}
            />
          </Spin>
          <Spin spinning={loadingUserData}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('deals')}
              description={thousand(userSummary?.totalDeals)}
            />
          </Spin>
          <Spin spinning={loadingUserData}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('installs')}
              description={thousand(userSummary?.totalInstalls)}
            />
          </Spin>
          <Spin spinning={loadingUserData}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('total_sytem_size')}
              description={thousand(userSummary?.systemSizeTotal)}
            />
          </Spin>
          <Spin spinning={loadingUserData}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('epc_total')}
              description={toCurrency(userSummary?.epcTotal)}
            />
          </Spin>
          <Spin spinning={loadingUserData}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('closer_commision_total')}
              description={toCurrency(userSummary?.closerCommisionTotal)}
            />
          </Spin>
          <Spin spinning={loadingUserData}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('average_epc')}
              description={toCurrency(userSummary?.averageEpc)}
            />
          </Spin>
          <Spin spinning={loadingUserData}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('average_system_size')}
              description={thousand(userSummary?.averageSystemSize)}
            />
          </Spin>
          <Spin spinning={loadingUserData}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('average_closer_commision')}
              description={toCurrency(userSummary?.averageCloserCommision)}
            />
          </Spin>
        </div>
      </Modal>
      <Modal
        width="80%"
        title={filter === 0 ? `${t('top_closers')} (${t('agreements')})` : (filter === 1 ? `${t('top_closers')} (${t('installs')})` : `${t('top_closers')} (${t('signed')})`)}
        open={showTable}
        onCancel={() => setShowTable(false)}
        footer={[]}
      >
        <Table
          columns={[
            {
              title: t('closer'),
              align: 'left',
              className: 'text-sm',
              sorter: (a, b) => a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase()),
              render: (row) => (
                <p>{row.label}</p>
              ),
            },
            {
              title: t('count'),
              align: 'left',
              className: 'text-sm',
              sorter: (a, b) => a.count - b.count,
              render: (row) => (
                <p>{thousand(row.count)}</p>
              ),
            },
          ]}
          dataSource={reportData}
          pagination={false}
          bordered
        />
      </Modal>
    </div>
  );
}

export default TopClosersGraph;
